import { Link, useLocation } from "react-router-dom";
import { useAuth, useTheme } from "@/App";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  MapPin,
  Image,
  Megaphone,
  Target,
  BarChart3,
  Receipt,
  Settings,
  Building2,
  Users,
  ChevronLeft,
  ChevronRight,
  LogOut,
  Radio,
  Monitor,
  Brain,
  UserCircle,
  Archive,
  BookOpen,
  Tags,
  Palette,
  FileEdit,
  DollarSign,
  UsersRound,
  Sparkles
} from "lucide-react";

// Booma Partner Portal logo (black on transparent; inverted to white in dark theme)
const BOOMA_LOGO = "/booma-partner-portal-trans-bg.png";

const mainNavItems = [
  { path: "/", label: "Dashboard", icon: LayoutDashboard },
  { path: "/campaigns", label: "Campaigns", icon: Megaphone, advertiserOnly: true },
  { path: "/campaigns/archived", label: "Archived", icon: Archive, advertiserOnly: true, subItem: true },
  { path: "/creatives", label: "Creatives", icon: Image, advertiserOnly: true },
  { path: "/creatives/assets", label: "Company Assets", icon: Palette, advertiserOnly: true, subItem: true },
  { path: "/locations", label: "Locations", icon: MapPin, advertiserOnly: true },
  { path: "/analytics", label: "Analytics", icon: BarChart3 },
  { path: "/billing", label: "Billing", icon: Receipt },
  { path: "/resources", label: "Resources", icon: BookOpen },
];

const adminNavItems = [
  { path: "/admin/advertisers", label: "Advertisers", icon: Building2 },
  { path: "/admin/categories", label: "Categories", icon: Tags },
  { path: "/admin/account-requests", label: "Account Requests", icon: FileEdit },
  { path: "/admin/tier-pricing", label: "Tier Pricing", icon: DollarSign },
  { path: "/admin/ai-models", label: "AI Models", icon: Sparkles },
  { path: "/attribution", label: "Attribution", icon: Brain },  // Admin only now
  { path: "/ad-serving", label: "Ad Serving", icon: Radio },
  { path: "/drive-panel", label: "Drive Panel", icon: Monitor, external: true },
  { path: "/admin/users", label: "Users", icon: Users },
];

const bottomNavItems = [
  { path: "/profile", label: "Profile", icon: UserCircle, advertiserOnly: true },
  { path: "/team", label: "Team", icon: UsersRound, advertiserOnly: true },
  { path: "/settings", label: "Settings", icon: Settings },
];

export default function Sidebar({ open, setOpen }) {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { theme } = useTheme();

  const NavLink = ({ item }) => {
    const isActive = location.pathname === item.path;
    const Icon = item.icon;

    // External links open in new tab
    if (item.external) {
      return (
        <a
          href={item.path}
          target="_blank"
          rel="noopener noreferrer"
          className={cn(
            "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200",
            "hover:bg-secondary/50 text-muted-foreground hover:text-foreground"
          )}
          data-testid={`nav-${item.path.replace(/\//g, '-') || 'home'}`}
        >
          <Icon className="h-5 w-5 flex-shrink-0" />
          {open && (
            <span className="font-medium text-sm flex items-center gap-1">
              {item.label}
              <svg className="w-3 h-3 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </span>
          )}
        </a>
      );
    }

    return (
      <Link
        to={item.path}
        className={cn(
          "flex items-center gap-3 rounded-lg transition-all duration-200",
          "hover:bg-secondary/50",
          item.subItem ? "px-3 py-2 ml-4" : "px-3 py-2.5",
          isActive && "bg-primary/10 text-primary border border-primary/20",
          !isActive && "text-muted-foreground hover:text-foreground"
        )}
        data-testid={`nav-${item.path.replace(/\//g, '-') || 'home'}`}
      >
        <Icon className={cn(
          "flex-shrink-0", 
          item.subItem ? "h-4 w-4" : "h-5 w-5",
          isActive && "text-primary"
        )} />
        {open && <span className={cn("font-medium", item.subItem ? "text-xs" : "text-sm")}>{item.label}</span>}
      </Link>
    );
  };

  return (
    <aside
      className={cn(
        "fixed left-0 top-0 h-screen bg-card border-r border-border flex flex-col transition-all duration-300 z-50",
        open ? "w-64" : "w-20"
      )}
      data-testid="sidebar"
    >
      {/* Logo */}
      <div className="p-4 border-b border-border">
        <Link to="/" className="flex items-center gap-3">
          <img 
            src={BOOMA_LOGO}
            alt="Booma Partner Portal"
            className={cn(
              "rounded-lg transition-all duration-300",
              open ? "h-10 w-auto" : "h-10 w-10 object-cover object-left",
              theme === 'dark' && "invert"
            )}
          />
        </Link>
      </div>

      {/* Toggle Button */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute -right-3 top-20 h-6 w-6 rounded-full border border-border bg-card shadow-md"
        onClick={() => setOpen(!open)}
        data-testid="sidebar-toggle"
      >
        {open ? <ChevronLeft className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
      </Button>

      {/* Main Nav */}
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {mainNavItems
          .filter(item => !item.advertiserOnly || !isAdmin)
          .map(item => (
            <NavLink key={item.path} item={item} />
          ))}

        {/* Admin Section */}
        {isAdmin && (
          <>
            <div className="pt-4 pb-2">
              {open && (
                <p className="px-3 text-xs text-muted-foreground uppercase tracking-wider font-semibold">
                  Admin
                </p>
              )}
              {!open && <div className="border-t border-border mx-3" />}
            </div>
            {adminNavItems.map(item => (
              <NavLink key={item.path} item={item} />
            ))}
          </>
        )}
      </div>

      {/* Bottom Nav */}
      <div className="p-3 border-t border-border space-y-1">
        {bottomNavItems
          .filter(item => !item.advertiserOnly || !isAdmin)
          .map(item => (
            <NavLink key={item.path} item={item} />
          ))}
        
        <Button
          variant="ghost"
          className={cn(
            "w-full justify-start gap-3 px-3 py-2.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10",
            !open && "justify-center"
          )}
          onClick={logout}
          data-testid="logout-btn"
        >
          <LogOut className="h-5 w-5 flex-shrink-0" />
          {open && <span className="font-medium text-sm">Logout</span>}
        </Button>
      </div>

      {/* User Info */}
      {open && user && (
        <div className="p-4 border-t border-border">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center">
              <span className="text-sm font-semibold text-primary">
                {user.name?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="overflow-hidden">
              <p className="text-sm font-medium truncate">{user.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user.email}</p>
            </div>
          </div>
        </div>
      )}
    </aside>
  );
}
